import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const StarRating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const partialFill = (rating % 1) * 100; // Percentage of partial star
  
  return (
    <div className="flex justify-center mt-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        if (star <= fullStars) {
          // Fully filled star
          return (
            <svg
              key={star}
              className="w-4 h-4 text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          );
        } else if (star === fullStars + 1 && partialFill > 0) {
          // Partially filled star
          return (
            <div key={star} className="relative w-4 h-4">
              <svg
                className="w-4 h-4 text-gray-300 absolute"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <svg
                className="w-4 h-4 text-yellow-400 absolute"
                fill="currentColor"
                viewBox="0 0 20 20"
                style={{ clipPath: `inset(0 ${100 - partialFill}% 0 0)` }}
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          );
        } else {
          // Empty star
          return (
            <svg
              key={star}
              className="w-4 h-4 text-gray-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          );
        }
      })}
    </div>
  );
};

const CustomerSurveyForm = () => {
  const { token } = useParams();
  const [ratings, setRatings] = useState({
    industryKnowledge: 5.0,
    responsiveness: 5.0,
    quality: 5.0,
    value: 5.0
  });
  const [comments, setComments] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewee, setReviewee] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`/api/profile/survey/info/${token}`)
      .then((r) => { if (r.data?.ok) setReviewee(r.data); })
      .catch(() => {});
  }, [token]);

  const handleRatingChange = (category, value) => {
    setRatings({
      ...ratings,
      [category]: parseFloat(value)
    });
  };

  const calculateAverage = () => {
    const sum = Object.values(ratings).reduce((a, b) => a + b, 0);
    return Math.round((sum / 4) * 100) / 100;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`/api/profile/survey/submit`, {
        token,
        ratings: {
          industryKnowledge: ratings.industryKnowledge,
          responsiveness: ratings.responsiveness,
          quality: ratings.quality,
          value: ratings.value
        },
        comments
      });
      setShowSuccess(true);
    } catch (err) {
      alert(err.response?.data?.error || "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4 pt-[0px] pb-8">
      <div className="relative bg-white rounded-lg w-full max-w-md overflow-hidden 
      shadow-[5px_5px_0_0_rgba(0,174,247,0.3),0_0_15px_rgba(0,0,0,0.1)]">
        <div className="relative z-10 p-4">
          {showSuccess ? (
            <div className="text-center py-4">
              <div className="text-green-600 font-semibold text-md mb-1">
                ✓ Thank you for your feedback!
              </div>
              <p className="text-sm text-gray-600">
                Your review has been submitted successfully
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviewee && (reviewee.firstName || reviewee.businessName) && (() => {
                const name = [reviewee.firstName, reviewee.lastName].filter(Boolean).join(' ');
                const profileHref = reviewee.profileSlug ? `/u/${reviewee.profileSlug}` : null;
                return (
                  <p className="text-sm text-gray-700 text-center">
                    You are completing a review of{' '}
                    {profileHref ? (
                      <a href={profileHref} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#00aef7] hover:underline">{name}</a>
                    ) : (
                      <span className="font-semibold">{name}</span>
                    )}
                    {reviewee.businessName ? <> with <span className="font-semibold">{reviewee.businessName}</span></> : null}
                    {' '}on eHandshake.com
                  </p>
                );
              })()}
              <h1 className="text-lg font-semibold text-center">
                Rate Your Experience
              </h1>
              <p className="text-xs text-gray-600 text-center mb-2">
                Please rate us on the following categories (1-5 scale)
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Industry Knowledge */}
                <div>
                  <label className="block text-xs font-medium mb-0.5">
                    Industry Knowledge
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.01"
                    value={ratings.industryKnowledge}
                    onChange={(e) => handleRatingChange('industryKnowledge', e.target.value)}
                    className="w-full h-1.5 bg-blue-100 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-center">
                    <span className="text-xs font-medium text-blue-600">
                      {ratings.industryKnowledge.toFixed(1)}
                    </span>
                    <StarRating rating={ratings.industryKnowledge} />
                  </div>
                </div>

                {/* Responsiveness */}
                <div>
                  <label className="block text-xs font-medium mb-0.5">
                    Responsiveness
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.01"
                    value={ratings.responsiveness}
                    onChange={(e) => handleRatingChange('responsiveness', e.target.value)}
                    className="w-full h-1.5 bg-blue-100 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-center">
                    <span className="text-xs font-medium text-blue-600">
                      {ratings.responsiveness.toFixed(1)}
                    </span>
                    <StarRating rating={ratings.responsiveness} />
                  </div>
                </div>

                {/* Product/Service Quality */}
                <div>
                  <label className="block text-xs font-medium mb-0.5">
                    Product/Service Quality
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.01"
                    value={ratings.quality}
                    onChange={(e) => handleRatingChange('quality', e.target.value)}
                    className="w-full h-1.5 bg-blue-100 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-center">
                    <span className="text-xs font-medium text-blue-600">
                      {ratings.quality.toFixed(1)}
                    </span>
                    <StarRating rating={ratings.quality} />
                  </div>
                </div>

                {/* Value for Money */}
                <div>
                  <label className="block text-xs font-medium mb-0.5">
                    Value for the Money
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="0.01"
                    value={ratings.value}
                    onChange={(e) => handleRatingChange('value', e.target.value)}
                    className="w-full h-1.5 bg-blue-100 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-center">
                    <span className="text-xs font-medium text-blue-600">
                      {ratings.value.toFixed(1)}
                    </span>
                    <StarRating rating={ratings.value} />
                  </div>
                </div>
              </div>

              {/* Average Rating Display */}
              <div className="mt-2 p-2 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-gray-600">Current Average Rating</p>
                <p className="text-lg font-bold text-blue-600">
                  {calculateAverage().toFixed(2)}
                </p>
                <StarRating rating={calculateAverage()} />
              </div>

              {/* Comments */}
              <div className="mt-2">
                <label className="block text-xs font-medium mb-0.5">
                  Additional Comments
                </label>
                <textarea
                  rows="3"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Share your overall experience..."
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full mt-3 py-1.5 px-3 rounded text-white font-medium ${
                  isSubmitting ? 'bg-[#00aef7]' : 'bg-[#00aef7] hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export { StarRating }; // Named export (new line)
export default CustomerSurveyForm;